import type { SubmitHandler } from 'react-hook-form';
import { useFormContext } from 'react-hook-form';
import type { InferType } from 'yup';
import type { tikTokSchema } from '../validation/tiktok.schema';
import { memo, Suspense, useCallback, useEffect, useMemo, useState } from 'react';
import { useCreateTikTokAd } from '../api/createTikTokAd';
import { AdCreationProgress, AddPackButton, CircularProgress, ConfirmationDialog } from 'components/common';
import useJobStatusCheck from 'hooks/useJobStatusCheck';
import { JobResultTable } from './TikTokFormComponents';
import type { StepInitStatus, StepType } from 'components/layout';
import { FormNavigation, StepperPaper } from 'components/layout';
import useStepper from 'hooks/useStepper';
import ChooseCampaign from './ChooseCampaign';
import ChooseCreative from './ChooseCreative';
import ChooseAdSet from './ChooseAdSet';
import AdSettings from './AdSettings';
import { useEnqueueSnackbar, useLocationParams } from 'hooks';
import AppAccount from 'features/tiktok/components/AppAccount';
import useCreateTikTokAdParams from '../hooks/useCreateTikTokAdParams';
import useStepHistorySync from '../hooks/useStepHistorySync';
import datadogActions, { DATADOG_EVENT } from 'lib/datadog';
import { useGlobalSettings } from 'providers';
import type { AppCode } from '../../../constants';
import { APP_LIST } from '../../../constants';
import { Box } from '@mui/system';
import DeepLinkInitializer from './DeepLinkInitializer';
import { getDefaultTikTokPack } from '../utils/buildPacksFromCreativesByPack';

export type TikTokFormValues = InferType<typeof tikTokSchema>;

const TikTokForm = memo(() => {
  const { onShowErrorAlert } = useEnqueueSnackbar();
  const { selectedApp } = useGlobalSettings();
  const {
    app: appParam,
    adAccount: adAccountParam,
    creativesByPack: creativesByPackParam,
    creativesFormat: creativesFormatParam,
    selectedCampaigns: selectedCampaignsParam,
    selectedAdSets: selectedAdSetsParam,
    step: stepParam,
    setStep: setStepParam,
    setApp: setAppParam,
    setAdAccount: setAdAccountParam,
    setCreativesByPack: setCreativesByPackParam,
    setSelectedCampaigns: setSelectedCampaignsParam,
    setSelectedAdSets: setSelectedAdSetsParam,
    setFilterCampaignsByName: setFilterCampaignsByNameParam,
    setFilterAdSetsByName: setFilterAdSetsByNameParam,
    resetLocationParamsToStart,
  } = useLocationParams();

  const {
    watch,
    trigger,
    reset: resetForm,
    handleSubmit,
    setValue,
    formState: { isValid },
  } = useFormContext<TikTokFormValues>();
  const selectedFormApp = watch('app');

  const initialSteps = useMemo<StepType[]>(
    () => [
      { title: 'Ad Account', component: AppAccount },
      { title: 'Creatives', component: ChooseCreative },
      { title: 'Campaign', component: ChooseCampaign },
      { title: 'Ad sets', component: ChooseAdSet },
      { title: 'Ad settings', component: AdSettings },
    ],
    [],
  );

  const creativesCompatible = creativesFormatParam !== 'flat';
  const hasUrlCreatives =
    creativesCompatible &&
    creativesByPackParam.some((group) => group.creatives.length > 0);

  const initialStep = useMemo(() => {
    const target = Math.min(stepParam, initialSteps.length - 1);
    if (target <= 0 || !creativesCompatible) return 0;

    for (let s = 0; s < target; s++) {
      if (s === 0 && !adAccountParam) return 0;
      if (s === 1 && !hasUrlCreatives) return 1;
      if (s === 2 && !selectedCampaignsParam.length) return 2;
      if (s === 3 && !selectedAdSetsParam.length) return 3;
    }
    return target;
  }, [
    adAccountParam,
    creativesCompatible,
    hasUrlCreatives,
    initialSteps.length,
    selectedAdSetsParam.length,
    selectedCampaignsParam.length,
    stepParam,
  ]);

  const { currentStep, resetSteps, prevStep, nextStep, goToStep, StepComponent } =
    useStepper({ initialSteps, defaultStep: initialStep });

  const [isInitializing, setIsInitializing] = useState(
    initialStep > 0 || !creativesCompatible || hasUrlCreatives,
  );
  const [initStepStatuses, setInitStepStatuses] = useState<StepInitStatus[]>([]);

  const handleInitComplete = useCallback(() => {
    setIsInitializing(false);
  }, []);

  const handleStatusChange = useCallback((statuses: StepInitStatus[]) => {
    setInitStepStatuses(statuses);
  }, []);

  const stepsWithStatus = useMemo<StepType[]>(
    () =>
      isInitializing
        ? initialSteps.map((step, i) => ({
            ...step,
            initStatus: initStepStatuses[i],
          }))
        : initialSteps,
    [isInitializing, initialSteps, initStepStatuses],
  );

  const stepperActiveStep = useMemo(() => {
    if (!isInitializing) return currentStep;
    const loadingIdx = initStepStatuses.findIndex((s) => s === 'loading');
    if (loadingIdx >= 0) return loadingIdx;
    const failedIdx = initStepStatuses.findIndex((s) => s === 'failed');
    if (failedIdx >= 0) return failedIdx;
    return initialStep;
  }, [isInitializing, initStepStatuses, currentStep, initialStep]);

  const revalidateForStep = useCallback(
    async (targetStep: number) => {
      setValue('currentStep', targetStep);
      await trigger();
    },
    [setValue, trigger],
  );

  const {
    data: jobStatusResponse,
    runJubStatusCheck,
    isLoading: isJobStatusChecking,
    isFinished: isJobStatusFinished,
    clearJobStatus,
  } = useJobStatusCheck();

  const handleInitReset = useCallback(() => {
    setIsInitializing(false);
    setInitStepStatuses([]);
    goToStep(0);
    resetForm({
      currentStep: 0,
      app: selectedFormApp ?? '',
      adType: 'SINGLE_VIDEO',
      accountId: '',
      selectedAdGroups: [],
      selectedCampaigns: [],
      selectedCampaignsOnly: false,
      selectedCreatives: [],
      packsWithCreatives: [getDefaultTikTokPack()],
      activePacksWithCreatives: 0,
      activePackTag: '',
      filterCreativeByName: '',
      filterCampaignsByName: [],
      filterAdSetsByName: [],
      $adSetsData: [],
    });
    clearJobStatus();
  }, [clearJobStatus, goToStep, resetForm, selectedFormApp]);

  const createTikTokAd = useCreateTikTokAd({
    config: {
      onSuccess: (r) => {
        datadogActions.trackDatadogAction(DATADOG_EVENT.createTikTokAdSuccess, {
          jobId: r.job_id as string,
        });
        runJubStatusCheck(r.job_id as string);
      },
      onError: (err) => {
        datadogActions.trackDatadogError(err, { source: 'create_tiktok_ad_error' });

        if (err?.cause) {
          onShowErrorAlert(
            (err.cause as { details?: string })?.details ?? 'Error occur',
            { delayAlert: 3500 },
          );
          console.error(err.cause);
          return;
        }

        onShowErrorAlert('Internal Error');
      },
    },
  });

  const { params: createTikTokAdParams } = useCreateTikTokAdParams();

  useEffect(() => {
    setValue('currentStep', currentStep, { shouldValidate: true });
  }, [currentStep, setValue]);

  useStepHistorySync({
    stepParam,
    currentStep,
    stepCount: initialSteps.length,
    goToStep,
    revalidateForStep,
  });

  useEffect(() => {
    if (!selectedFormApp) return;

    const nextApp = String(selectedFormApp) as AppCode;
    if (appParam === nextApp) return;
    if (!APP_LIST.some(({ code }) => code === nextApp)) return;

    setAppParam(nextApp);
  }, [selectedFormApp, appParam, setAppParam]);

  useEffect(() => {
    if (
      !selectedApp ||
      String(selectedFormApp ?? '') === String(selectedApp)
    ) {
      return;
    }

    // URL `app` already matches the form: the sidebar selector catching up
    // after a deep link is not a user app switch, so keep URL selections.
    if (
      appParam &&
      String(appParam) === String(selectedFormApp) &&
      String(appParam) !== String(selectedApp)
    ) {
      return;
    }

    resetForm({
      currentStep: 0,
      app: selectedApp,
      adType: 'SINGLE_VIDEO',
      accountId: '',
      selectedAdGroups: [],
      selectedCampaigns: [],
      selectedCampaignsOnly: false,
      selectedCreatives: [],
      packsWithCreatives: [getDefaultTikTokPack()],
      activePacksWithCreatives: 0,
      activePackTag: '',
      filterCreativeByName: '',
      filterCampaignsByName: [],
      filterAdSetsByName: [],
      $adSetsData: [],
    });
    resetSteps();
    setAdAccountParam(undefined);
    setCreativesByPackParam([]);
    setSelectedCampaignsParam([]);
    setSelectedAdSetsParam([]);
    setFilterCampaignsByNameParam([]);
    setFilterAdSetsByNameParam([]);
    setStepParam(0);
    clearJobStatus();
  }, [
    clearJobStatus,
    resetForm,
    resetSteps,
    selectedApp,
    selectedFormApp,
    appParam,
    setAdAccountParam,
    setCreativesByPackParam,
    setFilterAdSetsByNameParam,
    setFilterCampaignsByNameParam,
    setSelectedAdSetsParam,
    setSelectedCampaignsParam,
    setStepParam,
  ]);

  useEffect(() => {
    const stepTitle = initialSteps[currentStep]?.title;
    if (!stepTitle) {
      return;
    }

    datadogActions.trackDatadogAction(DATADOG_EVENT.tikTokFormStepChange, {
      stepTitle,
      stepIndex: currentStep,
    });
  }, [currentStep, initialSteps]);

  const onSubmit = useCallback<SubmitHandler<TikTokFormValues>>(() => {
    try {
      datadogActions.trackDatadogAction(DATADOG_EVENT.tikTokFormSubmitAttempt, {
        app: createTikTokAdParams.app,
        adAccountId: createTikTokAdParams.ad_account_id,
        campaignTemplatesCount: createTikTokAdParams.campaign_templates?.length ?? 0,
        creativePacksCount: createTikTokAdParams.creative_packs?.length ?? 0,
        adSettingsCount: createTikTokAdParams.ad_settings?.length ?? 0,
      });
      createTikTokAd.mutate(createTikTokAdParams);
    } catch (error) {
      datadogActions.trackDatadogError(error as Error, {
        source: 'tiktok_form_submit_try_catch',
      });
      onShowErrorAlert('Error during TikTok ad creation', {
        delayAlert: 3500,
      });
      console.error('Error during TikTok ad creation:', error);
    }
  }, [createTikTokAd, createTikTokAdParams, onShowErrorAlert]);

  const handleNextStep = useCallback(async () => {
    const valid = await trigger();
    if (!valid) return;

    if (currentStep === initialSteps.length - 1) {
      void handleSubmit(onSubmit)();
    } else {
      nextStep();
      setStepParam(currentStep + 1, 'pushIn');
      void revalidateForStep(currentStep + 1);
    }
  }, [
    nextStep,
    trigger,
    handleSubmit,
    onSubmit,
    initialSteps,
    currentStep,
    revalidateForStep,
    setStepParam,
  ]);

  const handlePrevStep = useCallback(() => {
    const target = Math.max(currentStep - 1, 0);
    prevStep();
    setStepParam(target, 'pushIn');
    void revalidateForStep(target);
  }, [prevStep, revalidateForStep, setStepParam, currentStep]);

  const handleRestart = useCallback(() => {
    datadogActions.trackDatadogAction(DATADOG_EVENT.tikTokFormRestart);
    resetLocationParamsToStart();
    resetForm();
    resetSteps();
    setStepParam(initialStep);
    clearJobStatus();
  }, [
    resetLocationParamsToStart,
    resetForm,
    resetSteps,
    setStepParam,
    initialStep,
    clearJobStatus,
  ]);

  const handleLaunchMoreAds = useCallback(() => {
    window.open('/tiktok', '_blank', 'noopener');
  }, []);

  const isNavigationLocked =
    createTikTokAd.isPending || !!isJobStatusChecking || isInitializing;
  const isDisabledNext = !isValid || isNavigationLocked || isJobStatusFinished;

  return (
    <StepperPaper
      title={'TikTok Ads Automation'}
      steps={stepsWithStatus}
      activeStep={stepperActiveStep}
    >
      <>
        {isJobStatusChecking ? (
          <AdCreationProgress onLaunchMoreAds={handleLaunchMoreAds} />
        ) : (
          <Suspense fallback={<CircularProgress />}>
            {isJobStatusFinished && jobStatusResponse ? (
              <JobResultTable rows={jobStatusResponse.result} />
            ) : isInitializing ? (
              <Box sx={{ display: 'flex', width: '100%', height: '100%', flex: 1 }}>
                <DeepLinkInitializer
                  targetStep={initialStep}
                  onComplete={handleInitComplete}
                  onReset={handleInitReset}
                  onStatusChange={handleStatusChange}
                />
              </Box>
            ) : (
              <StepComponent />
            )}
          </Suspense>
        )}
        <FormNavigation
          isJobStatusFinished={isJobStatusFinished}
          onRestart={handleRestart}
          isDisabledRestart={!currentStep && !isJobStatusFinished}
          onBack={handlePrevStep}
          isDisabledBack={!currentStep || isNavigationLocked || isJobStatusFinished}
          onNext={handleNextStep}
          isDisabledNext={isDisabledNext}
          titleNext={
            currentStep === initialSteps.length - 1 ? 'Create Ad' : 'Next'
          }
          childrenOnTheRightSide={
            <>{currentStep === 1 && !isInitializing ? <AddPackButton /> : null}</>
          }
        />
      </>
      {!jobStatusResponse?.result && (
        <ConfirmationDialog
          isUnblock={createTikTokAd.isSuccess && isJobStatusFinished}
        />
      )}
    </StepperPaper>
  );
});

TikTokForm.displayName = 'TikTokForm';

export default TikTokForm;
